import * as Sentry from '@sentry/react';

import { getCookie } from 'utils/common';
import {
  HIGH_SIGNAL_PRODUCT_STATUS_TAG_KEYS,
  HIGH_SIGNAL_PRODUCT_STATUS_TAGS,
  IMPORT_MERCHANT_ENABLED_STATUSES,
  PRODUCT_STATUS_CONTEXT_NAME,
} from './constant';
import type {
  SentrySlot,
  SentryEventLike,
  SentryMerchantSessionTags,
  SentryProductStatusMap,
  SentryProductStatusNorm,
} from './types';

export type {
  SentrySlot,
  SentryMerchantSessionTags,
  SentryProductStatusMap,
  SentryProductStatusNorm,
} from './types';

const SLOT_COOKIE_NAME = 'cf-slot-merchantwebapp';

/** FP-7707: reads the infra-set blue/green slot cookie — authoritative for which build actually served this request. */
export const deriveSlotTag = (): SentrySlot =>
  getCookie(SLOT_COOKIE_NAME) === 'green' ? 'green' : 'blue';

let cached: SentryMerchantSessionTags = {};

export const getSentryMerchantTags = (): SentryMerchantSessionTags => ({
  ...cached,
});

export const normalizeProductStatus = (
  value: string | undefined | null,
): SentryProductStatusNorm => {
  if (!value || typeof value !== 'string') return 'unknown';
  const upper = value.trim().toUpperCase();
  if (upper === 'APPROVED') return 'approved';
  if (upper === 'REJECTED') return 'rejected';
  if (upper === 'UNDER_REVIEW') return 'under_review';
  return 'unknown';
};

export const deriveProductStatusTags = (
  productStatus: SentryProductStatusMap | null | undefined,
): Pick<
  SentryMerchantSessionTags,
  | 'pg_status'
  | 'epos_status'
  | 'router_status'
  | 'payouts_status'
  | 'riskshield_status'
  | 'isMerchantImportEnabled'
  | 'productStatus'
  | 'productStatusFetchedAt'
> => {
  const map =
    productStatus && typeof productStatus === 'object' ? productStatus : {};
  const fetchedAt = new Date().toISOString();

  const derived: ReturnType<typeof deriveProductStatusTags> = {
    productStatus: { ...map },
    productStatusFetchedAt: fetchedAt,
    isMerchantImportEnabled: (
      IMPORT_MERCHANT_ENABLED_STATUSES as readonly string[]
    ).includes(String(map.IMPC ?? '').toUpperCase()),
  };

  HIGH_SIGNAL_PRODUCT_STATUS_TAGS.forEach(({ tag, code }) => {
    derived[tag] = normalizeProductStatus(map[code]);
  });

  return derived;
};

const safeSetTag = (key: string, value: string): void => {
  try {
    Sentry.setTag(key, value);
  } catch {
    // Sentry may not be initialized yet (e.g. unit tests)
  }
};

const safeSetContext = (
  name: string,
  data: Record<string, string> | null,
): void => {
  try {
    Sentry.setContext(name, data);
  } catch {
    // ignore
  }
};

const buildProductStatusContext = (
  tags: SentryMerchantSessionTags,
): Record<string, string> | null => {
  if (!tags.productStatus || Object.keys(tags.productStatus).length === 0) {
    return null;
  }
  const ctx: Record<string, string> = {};
  Object.entries(tags.productStatus).forEach(([code, status]) => {
    if (status != null && status !== '') ctx[code] = String(status);
  });
  if (tags.productStatusFetchedAt) {
    ctx.fetched_at = tags.productStatusFetchedAt;
  }
  return Object.keys(ctx).length ? ctx : null;
};

export const syncSentryMerchantTags = (
  tags: SentryMerchantSessionTags,
): void => {
  cached = { ...cached, ...tags };

  if (typeof tags.isMerchantImportEnabled === 'boolean') {
    safeSetTag('isMerchantImportEnabled', String(tags.isMerchantImportEnabled));
  }

  if (tags.slot) {
    safeSetTag('slot', tags.slot);
  }

  HIGH_SIGNAL_PRODUCT_STATUS_TAG_KEYS.forEach(key => {
    if (tags[key]) safeSetTag(key, tags[key] as string);
  });

  if (tags.productStatus !== undefined) {
    safeSetContext(
      PRODUCT_STATUS_CONTEXT_NAME,
      buildProductStatusContext(cached),
    );
  }
};

export const syncSentryProductStatus = (
  productStatus: SentryProductStatusMap | null | undefined,
): void => {
  syncSentryMerchantTags(deriveProductStatusTags(productStatus));
};

/** Apply cached merchant tags + product-status context onto an outgoing event */
export const applySentryMerchantTags = (event: SentryEventLike): void => {
  const nextTags: Record<string, unknown> = { ...event.tags };

  if (typeof cached.isMerchantImportEnabled === 'boolean') {
    nextTags.isMerchantImportEnabled = String(cached.isMerchantImportEnabled);
  }

  if (cached.slot) {
    nextTags.slot = cached.slot;
  }

  HIGH_SIGNAL_PRODUCT_STATUS_TAG_KEYS.forEach(key => {
    if (cached[key]) nextTags[key] = cached[key];
  });

  event.tags = nextTags;

  const productCtx = buildProductStatusContext(cached);
  if (productCtx) {
    event.contexts = {
      ...event.contexts,
      [PRODUCT_STATUS_CONTEXT_NAME]: productCtx,
    };
  }
};
